import { Download } from "lucide-react";
import { Sheet } from "../../components/Sheet";
import { packageUrl } from "../../lib/api";
import { apiUrl } from "../../lib/env";
import type { RunStatus } from "../../lib/types";

/**
 * EXPORT CONFIRM — the one deliberate, ceremonial gesture.
 *
 * A real modal (Sheet), because the operator is committing to a deliverable. It restates exactly
 * what the package contains — accepted edits, kept originals, overridden advisories, and the fact
 * that zero protected-fact violations remain — and states, plainly, that this is EXPORT ONLY:
 * files are made available, nothing is sent. The download is the confirm button itself.
 */
export function ExportConfirm({
  run,
  accepted,
  kept,
  overridden,
  unaddressed,
  onCancel,
  onExported
}: {
  run: RunStatus;
  accepted: number;
  kept: number;
  overridden: number;
  unaddressed: number;
  onCancel: () => void;
  onExported: () => void;
}) {
  const rows: Array<[string, string]> = [
    ["Accepted edits", String(accepted)],
    ["Kept original", String(kept)],
    ["Advisories overridden", String(overridden)],
    ["Advisories not addressed", String(unaddressed)],
    ["Protected-fact violations", "0"]
  ];
  return (
    <Sheet
      title="Export document"
      testId="export-confirm-sheet"
      onClose={onCancel}
      footer={
        apiUrl ? (
          <a
            className="btn btn-primary btn-block"
            href={packageUrl(run.runId)}
            data-testid="export-download"
            onClick={onExported}
          >
            <Download aria-hidden="true" />
            Export document
          </a>
        ) : (
          <button type="button" className="btn btn-primary btn-block" disabled>
            Export needs the backend
          </button>
        )
      }
    >
      <div className="export-body">
        <p className="export-lede">
          The package for <strong>{run.input.organization || "this run"}</strong> is ready. This is an
          export — files are made available to you and nothing is sent anywhere.
        </p>
        <dl className="export-payload" data-testid="export-payload">
          {rows.map(([label, value]) => (
            <div key={label} className="export-row">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        {overridden ? (
          <p className="export-foot">
            {overridden} advisory suggestion{overridden === 1 ? "" : "s"} you kept as-is {overridden === 1 ? "is" : "are"} recorded, with your reason, in the run package.
          </p>
        ) : null}
        {!apiUrl ? (
          <p className="export-foot">This is a local preview — no backend to build the package. Recorded locally, not verified by QA.</p>
        ) : null}
      </div>
    </Sheet>
  );
}
